import { manualOrderSchema, type ManualOrderInput } from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Field } from "~/components/ui/field";
import { Input } from "~/components/ui/input";
import { Page, PageHeader } from "~/components/ui/page";
import { DocumentEditor, toApiLines, type EditorState } from "~/features/sales/document-editor";
import { companyQuery } from "~/features/sales/send-email";
import { parsePercent } from "~/features/sales/status";
import { api, unwrap } from "~/lib/api";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { schemaErrors, type ErrorMap } from "~/lib/forms";
import { useT } from "~/lib/i18n";

export const Route = createFileRoute("/_app/orders/new")({ component: NewOrder });

interface Draft extends EditorState {
  reference: string;
}

function toPayload(d: Draft): ManualOrderInput {
  return {
    customerId: d.customer?.id ?? "",
    externalReference: d.reference,
    globalDiscountBp: parsePercent(d.globalDiscount) ?? Number.NaN,
    shippingCents: d.shippingCents ?? 0,
    notes: d.notes,
    terms: d.terms,
    lines: toApiLines(d.lines),
  };
}

function NewOrder() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const company = useQuery(companyQuery);
  const [draft, setDraft] = useState<Draft>({
    customer: null,
    lines: [],
    globalDiscount: "",
    shippingCents: 0,
    notes: "",
    terms: "",
    reference: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});
  const errors = { ...serverErrors, ...(submitted ? schemaErrors(manualOrderSchema, toPayload(draft)) : {}) };

  const create = useMutation({
    mutationFn: () => unwrap(api.orders.post(manualOrderSchema.parse(toPayload(draft)))),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["stock"] });
      toast.success(t("orders.created"));
      void navigate({ to: "/orders/$id", params: { id: order.id } });
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(schemaErrors(manualOrderSchema, toPayload(draft))).length) return;
    create.mutate();
  };

  return (
    <Page>
      <PageHeader
        back={
          <Link to="/orders" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
            <ChevronLeft className="size-4" aria-hidden />
            {t("orders.title")}
          </Link>
        }
        title={t("orders.new")}
        actions={
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            {t("common.actions.create")}
          </Button>
        }
      />
      <DocumentEditor
        value={draft}
        onChange={(v) => setDraft({ ...draft, ...v })}
        errors={errors}
        marginAlertBp={company.data?.marginAlertBp ?? 2000}
        header={
          <Field label={t("orders.fields.reference")} optionalLabel={t("common.states.optional")} className="sm:col-span-3">
            <Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} />
          </Field>
        }
      />
    </Page>
  );
}
